/**
 * 节点配置面板容器组件
 *
 * 作为所有节点配置面板的通用容器，提供统一的布局和交互模式
 * 支持：暂停时修改配置，配置保存后立即生效
 */

import React, { useCallback, useState, useEffect } from 'react';
import { clsx } from 'clsx';
import type { WorkflowNodeType } from '../../../types/workflow';
import { getNodeTypeDisplayName } from '../../../types/workflow';
import { ExecutionState } from '../../../types/workflow';
import { useWorkflowStore, useExecutionStore } from '../../../store/workflowStore';
import { StartNodeConfigPanel } from './StartNodeConfigPanel';
import { AgentNodeConfigPanel } from './AgentNodeConfigPanel';
import { LoopNodeConfigPanel } from './LoopNodeConfigPanel';
import { ConditionNodeConfigPanel } from './ConditionNodeConfigPanel';
import { InfoNodeConfigPanel } from './InfoNodeConfigPanel';

export interface NodeConfigPanelProps {
  /** 选中的节点 ID */
  nodeId: string | null;
  /** 关闭面板回调 */
  onClose?: () => void;
  /** 额外的类名 */
  className?: string;
}

// 节点类型图标
const NodeTypeIcons: Record<WorkflowNodeType, React.ReactNode> = {
  start: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  agent: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  loop: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  condition: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  note: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
};

// 节点类型颜色
const NodeTypeColors: Record<WorkflowNodeType, { bg: string; text: string }> = {
  start: { bg: 'bg-green-50', text: 'text-green-600' },
  agent: { bg: 'bg-paper-50', text: 'text-paper-700' },
  loop: { bg: 'bg-purple-50', text: 'text-purple-600' },
  condition: { bg: 'bg-amber-50', text: 'text-amber-600' },
  info: { bg: 'bg-cyan-50', text: 'text-cyan-600' },
  note: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

export const NodeConfigPanel: React.FC<NodeConfigPanelProps> = React.memo(({
  nodeId,
  onClose,
  className,
}) => {
  // 配置已更新提示状态
  const [showSavedToast, setShowSavedToast] = useState(false);
  
  // 从 store 获取节点数据
  const node = useWorkflowStore((state) =>
    nodeId ? state.nodes.find((n) => n.id === nodeId) : null
  );
  
  // 从 execution store 获取执行状态
  const executionState = useExecutionStore((state) => state.executionState);
  
  // 是否处于暂停状态
  const isPaused = executionState === ExecutionState.PAUSED || executionState === ExecutionState.STEP_WAITING;
  
  // 是否允许编辑（暂停时可编辑，其他状态下也可以编辑）
  const canEdit = true;
  
  // 监听工作流 store 的 isDirty 状态变化，显示保存提示
  const isDirty = useWorkflowStore((state) => state.isDirty);
  
  // 当暂停状态下保存配置时显示提示
  useEffect(() => {
    if (isPaused && !isDirty && showSavedToast) {
      // 保持显示一段时间后隐藏
      const timer = setTimeout(() => {
        setShowSavedToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isPaused, isDirty, showSavedToast]);
  
  // 当配置变更时设置保存提示
  useEffect(() => {
    if (isPaused && isDirty) {
      setShowSavedToast(true);
    }
  }, [isPaused, isDirty]);

  // 关闭面板
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // 没有选中节点时显示提示
  if (!nodeId || !node) {
    return (
      <div className={clsx(
        'w-80 h-full bg-white border-l border-gray-200',
        'flex flex-col items-center justify-center text-gray-400',
        className,
      )}>
        <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
        <p className="text-sm">点击节点以编辑配置</p>
      </div>
    );
  }

  // 获取节点类型
  const nodeType = node.type as WorkflowNodeType;
  const colors = NodeTypeColors[nodeType] || NodeTypeColors.note;
  const icon = NodeTypeIcons[nodeType];
  const typeName = getNodeTypeDisplayName(nodeType);

  // 根据节点类型渲染对应的配置面板
  const renderConfigPanel = () => {
    switch (nodeType) {
      case 'start':
        return <StartNodeConfigPanel nodeId={nodeId} />;
      case 'agent':
        return <AgentNodeConfigPanel nodeId={nodeId} />;
      case 'loop':
        return <LoopNodeConfigPanel nodeId={nodeId} />;
      case 'condition':
        return <ConditionNodeConfigPanel nodeId={nodeId} />;
      case 'info':
        return <InfoNodeConfigPanel nodeId={nodeId} />;
      case 'note':
        return (
          <div className="p-4 text-sm text-gray-500 italic">
            注释节点无需配置
          </div>
        );
      default:
        return (
          <div className="p-4 text-sm text-gray-500 italic">
            未知节点类型
          </div>
        );
    }
  };

  return (
    <div className={clsx(
      'w-80 h-full bg-white border-l border-gray-200',
      'flex flex-col',
      isPaused && 'ring-2 ring-warning-300 ring-inset',
      className,
    )}>
      {/* 面板头部 */}
      <div className={clsx(
        'flex items-center gap-3 px-4 py-3 border-b border-gray-200',
        colors.bg,
      )}>
        <span className={colors.text}>{icon}</span>
        <span className={clsx('text-sm font-medium flex-1', colors.text)}>
          {typeName}配置
        </span>
        
        {/* 暂停状态标识 */}
        {isPaused && (
          <span className="text-xs text-warning-600 bg-warning-100 px-2 py-0.5 rounded-full font-medium">
            调试中
          </span>
        )}
        
        {/* 关闭按钮 */}
        <button
          onClick={handleClose}
          className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="关闭配置面板"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* 暂停状态提示条 */}
      {isPaused && (
        <div className="px-4 py-2 bg-warning-50 border-b border-warning-100 text-xs text-warning-700">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-warning-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>暂停中 - 修改的配置将在下一节点生效</span>
          </div>
        </div>
      )}

      {/* 配置内容 */}
      <div className={clsx(
        'flex-1 overflow-y-auto',
        !canEdit && 'opacity-60 pointer-events-none',
      )}>
        {renderConfigPanel()}
      </div>
      
      {/* 配置已更新提示 */}
      {isPaused && showSavedToast && !isDirty && (
        <div className="absolute bottom-4 left-4 right-4 bg-success-500 text-white text-xs px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-slide-up">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>配置已更新，恢复执行后生效</span>
        </div>
      )}
    </div>
  );
});

NodeConfigPanel.displayName = 'NodeConfigPanel';

export default NodeConfigPanel;
